



















































































































































































































































/**
 * NOTE: When using the sync modifier, objects must always be passed with v-bind!
 *  ALl custom validations should evaluate to true to submit form. Invalid = false
 */
import { Component, Mixins, Watch } from "vue-property-decorator";
import Billing from "@/components/checkout/forms/Billing.vue";
import Contact from "@/components/checkout/forms/Contact.vue";
import Notes from "@/components/checkout/forms/Notes.vue";
import Payment from "@/components/checkout/forms/Payment.vue";
import Shipping from "@/components/checkout/forms/Shipping.vue";
import AccordionSectionCheckout from "@/components/elements/accordion/AccordionSectionCheckout.vue";
import { Address } from "@/models/address/address";
import {
  ChairOrderRecord,
  Order,
  OrderDetails,
  UmbrellaOrderRecord
} from "@/models/order/order";
import { NetSuiteCustomer, PaymentTerm } from "@/models/netsuite/netsuite";
import { CreatedType } from "@/models/order/helpers";
import { EventBus } from "@/event-bus";
import { GenericError } from "@/services/error_service";
import { NotificationService } from "@/services/notification_service";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import { GlobalActions, GlobalGetters } from "@/store/modules/global/types";
import { PriceLevel } from "@/models/netsuite/netsuite";
import {
  CustomChairOrder,
  CustomUmbrellaOrder
} from "@/models/configurator/custom_umbrella";
import { ProductType } from "@/models/products/product";
import { Mobile } from "@/mixins/mobile";
import { OrderMixin } from "@/mixins/orders/mixin";
import {
  hasCustomerDetails,
  loadCustomerDetails,
  loadPaymentTerms
} from "@/services/customer_details_prefetch";

// Module-level cache: survives component destroy/recreate during navigation.
// cachedForCustomerId records which order.customer_id the cache was written
// for, so a Review round-trip can be told apart from arriving at checkout for
// a different customer (FUPOF-191).
let cachedFullCustomer: NetSuiteCustomer | null = null;
let cachedPaymentTerms: PaymentTerm[] = [];
let cachedForCustomerId: string | null = null;

/**
 * FUPOF-191: the header sections below are handed to the sub-forms with
 * `v-bind.sync`, and Vue only wires an `update:<key>` listener for keys the
 * bound object already has when it renders. A section that arrives without one
 * of its optional keys therefore swallows every update for it, with no error —
 * the field simply refuses to stick.
 *
 * That is exactly what happened to the freight estimate: a draft's
 * `header_payload.shipping_details` comes back from the API with no
 * `request_freight_estimate`, so the Yes/No answer never left Shipping.vue,
 * vanished on the walk back from Review, and left the section permanently
 * invalid — which is what made REVIEW QUOTE look dead. Selecting an existing
 * contact lost `title`/`contact_id`/`send_email` the same way.
 *
 * So every section the form binds is built from these templates and the loaded
 * values merged on top, guaranteeing the full key set no matter how sparse the
 * source (store defaults, a draft header, a NetSuite drift report).
 */
const blankContactDetails = (): OrderDetails["contact_details"] => ({
  quote_name: "",
  company_name: "",
  title: "",
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  contact_id: null,
  send_email: false
});

const blankShippingDetails = (): NonNullable<
  OrderDetails["shipping_details"]
> => ({
  label: "",
  addressee: "",
  attention: "",
  address: { country: "US" } as Address,
  phone: "",
  email: "",
  freight_instructions: "",
  request_freight_estimate: null
});

const blankBillingDetails = (): NonNullable<
  OrderDetails["billing_details"]
> => ({
  addressee: "",
  attention: "",
  address: { country: "US" } as Address,
  phone: "",
  email: ""
});

const blankPaymentDetails = (): NonNullable<
  OrderDetails["payment_details"]
> => ({
  method: undefined
});

/**
 * The loaded section laid over a full-key template. `undefined` never wins, so
 * a key the source omits keeps the template's value (and, crucially, exists at
 * all). The nested address is merged rather than replaced for the same reason.
 */
const withAllKeys = <T extends Record<string, any>>(
  blank: T,
  loaded?: Partial<T> | null
): T => {
  const merged: Record<string, any> = { ...blank };
  if (loaded) {
    for (const key of Object.keys(loaded)) {
      const value = (loaded as Record<string, any>)[key];
      if (value === undefined) continue;
      merged[key] =
        key === "address" && value && typeof value === "object"
          ? { ...(blank as Record<string, any>).address, ...value }
          : value;
    }
  }
  return merged as T;
};

/** A validated section, its accordion index and the title shown on it. */
type CheckoutSection = "contact" | "billing" | "shipping" | "payment";

@Component({
  components: {
    AccordionSectionCheckout,
    Billing,
    Contact,
    Notes,
    Payment,
    Shipping
  }
})
export default class CheckoutForm extends Mixins(Mobile, OrderMixin) {
  @(namespace(StoreModule.Order).Action(OrderActions.AddShipping))
  addShipping!: (value: OrderDetails["shipping_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddBilling))
  addBilling!: (value: OrderDetails["billing_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddPayment))
  addPayment!: (value: OrderDetails["payment_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddContact))
  addContact!: (value: OrderDetails["contact_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddNotes))
  addNotes!: (value: string) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddOrderId))
  addOrderId!: (value: { id: string }) => Promise<void>;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetRole))
  userRole!: string;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  @(namespace(StoreModule.Order).Action(OrderActions.AssignCustomer))
  assignCustomer!: (value: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    order_id: string | null;
  }) => Promise<void>;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetPriceLevels))
  storePriceLevels!: PriceLevel[];
  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  addMultiplier!: (value: PriceLevel | null) => Promise<void>;

  protected loader: any = null;
  protected fullCustomer: NetSuiteCustomer | null = null;
  protected paymentTerms: PaymentTerm[] = [];
  /**
   * FUPOF-191 (FT-2/FT-3): the customer this form was last hydrated for.
   * null until the first initializeFromCustomer() run, which lets us tell an
   * edit session's initial hydration (keep the loaded header) apart from the
   * user swapping customers mid-edit (reset the header).
   */
  private hydratedForCustomerId: string | null = null;
  protected error = {
    billing: false,
    shipping: false,
    payment: false,
    contact: false
  };
  protected order: OrderDetails = {
    contact_details: blankContactDetails(),
    shipping_details: blankShippingDetails(),
    billing_details: blankBillingDetails(),
    payment_details: blankPaymentDetails(),
    notes: ""
  };
  async created(): Promise<void> {
    EventBus.$on("reviewQuote", this.goToReview);
    EventBus.$on("saveQuoteDraft", this.saveDraftFromCheckout);
    await this.initializeFromCustomer();
  }

  beforeDestroy(): void {
    EventBus.$off("reviewQuote", this.goToReview);
    EventBus.$off("saveQuoteDraft", this.saveDraftFromCheckout);
  }

  /**
   * FUPOF-191: Save Draft from the checkout footer. Unlike goToReview this
   * skips form validation — draft header fields are optional; they only
   * become required at submit.
   */
  protected async saveDraftFromCheckout(): Promise<void> {
    this.loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement(
          "h1",
          this.storeOrder.draft_id ? "Updating Draft" : "Saving Draft"
        )
      }
    );
    try {
      await this.saveDataToStore();
      const draft = await this.saveDraftAction();
      NotificationService.show(`Draft "${draft.title}" saved.`, "success");
    } catch (err) {
      GenericError.popup(err.message);
    } finally {
      if (this.loader) {
        this.loader.hide();
      }
    }
  }

  @Watch("storeOrder.customer_id")
  async onCustomerChanged(): Promise<void> {
    await this.initializeFromCustomer();
  }

  /**
   * Deep copy of one header section off the store. The sections are plain
   * JSON-safe objects (strings, numbers, a nested Address), so a structured
   * clone via JSON is sufficient and keeps the form detached from Vuex state.
   */
  private cloneSection<T>(section: T): T {
    return section ? JSON.parse(JSON.stringify(section)) : section;
  }

  /**
   * A detached copy of the order whose header sections carry every key the
   * sub-forms bind, whatever the source left out. See `withAllKeys` above for
   * why the missing keys matter.
   */
  private hydrateOrder(source: Order): OrderDetails {
    const order = Object.assign({}, { ...source }) as OrderDetails;
    order.contact_details = withAllKeys(
      blankContactDetails(),
      this.cloneSection(source.contact_details)
    );
    order.shipping_details = withAllKeys(
      blankShippingDetails(),
      this.cloneSection(source.shipping_details)
    );
    order.billing_details = withAllKeys(
      blankBillingDetails(),
      this.cloneSection(source.billing_details)
    );
    order.payment_details = withAllKeys(
      blankPaymentDetails(),
      this.cloneSection(source.payment_details)
    );
    return order;
  }

  async initializeFromCustomer(): Promise<void> {
    // When returning from OrderReview, restore form data and cached API data
    // without making any API calls or showing a loader.
    //
    // FUPOF-191: the cache is a module-level global that survives arbitrary
    // navigation, so only reuse it when it was written for the customer we're
    // actually checking out. Otherwise a Review visit for one customer leaves
    // stale data that short-circuits the fetch for the next customer (e.g.
    // Review "Hit the Deck", switch to "85N Sunny", check out → no
    // getNetSuiteCustomerById call). Comparing order.customer_id covers both
    // NetSuite customers (an id string) and general customers (""). Snapshot
    // then drop the cache so exactly one initialize consumes it, matched or not.
    const cachedCustomerId = cachedForCustomerId;
    const restoredFullCustomer = cachedFullCustomer;
    const restoredPaymentTerms = cachedPaymentTerms;
    if (cachedForCustomerId !== null) {
      cachedFullCustomer = null;
      cachedPaymentTerms = [];
      cachedForCustomerId = null;
    }
    if (
      cachedCustomerId !== null &&
      cachedCustomerId === (this.storeOrder.customer_id ?? "")
    ) {
      this.order = this.hydrateOrder(this.storeOrder);
      this.fullCustomer = restoredFullCustomer;
      this.paymentTerms = restoredPaymentTerms;
      this.hydratedForCustomerId = this.storeOrder.customer_id ?? null;
      return;
    }

    // FUPOF-191 (FT-3): when editing a saved quote, loadDraft has already put
    // header_payload's contact/shipping/billing/payment into the store — keep
    // those values instead of blanking them. But a customer swap *during* an
    // edit (header dropdown → assignCustomer without resetOrder) must still
    // reset, or the previous customer's details linger on the form.
    const currentCustomerId = this.storeOrder.customer_id ?? null;
    const isCustomerSwitch =
      this.hydratedForCustomerId !== null &&
      this.hydratedForCustomerId !== currentCustomerId;
    const preserveLoadedHeader =
      !!this.storeOrder.draft_id && !isCustomerSwitch;
    this.hydratedForCustomerId = currentCustomerId;

    // FUPOF-191: swapping the customer while standing on Quote Details is not
    // the start of a new quote — the fields the user already filled in that
    // describe the *quote* rather than the customer stay put. Only what came
    // off the old customer's record (contact person, addresses, payment terms)
    // is cleared below. Snapshot from `this.order` rather than the store so
    // edits that haven't been saved to Vuex yet survive too.
    const carriedQuoteFields = isCustomerSwitch
      ? {
          quote_name: this.order.contact_details?.quote_name ?? "",
          send_email: this.order.contact_details?.send_email ?? false,
          notes: this.order.notes ?? "",
          freight_instructions:
            this.order.shipping_details?.freight_instructions ?? "",
          request_freight_estimate:
            this.order.shipping_details?.request_freight_estimate ?? null
        }
      : null;

    // The details are usually already in hand — assignCustomer starts fetching
    // them the moment the customer is picked — so the overlay only goes up
    // when the request is still in flight (a customer selected right here on
    // this page, or a slow NetSuite).
    const netSuiteId = this.storeOrder.customer_netsuite_id;
    if (netSuiteId && !hasCustomerDetails(netSuiteId)) {
      this.loader = this.$loading.show(
        { opacity: 1 },
        {
          before: this.$createElement("h1", "Obtaining Customer Details")
        }
      );
    }
    this.fullCustomer = null;
    // hydrateOrder deep-clones the header sections, so the form's v-models
    // don't mutate store state outside a mutation (and get persisted by
    // vuex-persist), and fills in any key the source omitted.
    this.order = this.hydrateOrder(this.storeOrder);
    if (!preserveLoadedHeader) {
      // Reset all form data so stale data from a previous customer doesn't
      // carry over. On a mid-checkout customer switch the quote-level fields
      // captured above are written back in — losing the quote name (and the
      // notes and freight instructions along with it) on every customer change
      // is especially wrong for a quote that was already saved as a draft or
      // sent to NetSuite.
      this.order.contact_details = withAllKeys(blankContactDetails(), {
        quote_name: carriedQuoteFields?.quote_name ?? "",
        send_email: carriedQuoteFields?.send_email ?? false
      });
      this.order.billing_details = blankBillingDetails();
      this.order.shipping_details = withAllKeys(blankShippingDetails(), {
        freight_instructions: carriedQuoteFields?.freight_instructions ?? "",
        request_freight_estimate:
          carriedQuoteFields?.request_freight_estimate ?? null
      });
      // Always reset payment method — will be auto-set for existing customers below,
      // or manually selected by the user for new/general customers
      this.order.payment_details = blankPaymentDetails();
      // FUPOF-191 (FT-2): notes are persisted in the order store, so without
      // this they carry over from the previous quote. Clear the store too, in
      // case the user leaves checkout before saveDataToStore() runs.
      //
      // A duplicate is the exception: loadDraft applied the source quote's
      // header_payload.notes into the store, and a copy must keep them (its
      // address sections are still reset above — the /preview payload carries
      // notes but not contact/shipping/billing, which come from the customer).
      //
      // A customer switch is the other exception: the notes belong to the
      // quote, not to the customer that was just replaced.
      const isDuplicate =
        this.storeOrder.created_type === CreatedType.Duplicated;
      if (carriedQuoteFields) {
        this.order.notes = carriedQuoteFields.notes;
        await this.addNotes(carriedQuoteFields.notes);
      } else if (!isDuplicate) {
        this.order.notes = "";
        await this.addNotes("");
      }
    }
    if (netSuiteId) {
      try {
        // Resolves instantly when the prefetch already landed; otherwise this
        // awaits that same in-flight request rather than issuing a second one.
        const { customer, paymentTerms } = await loadCustomerDetails(
          netSuiteId
        );
        this.fullCustomer = customer;
        this.paymentTerms = paymentTerms;
        if (!this.order.contact_details.company_name) {
          this.order.contact_details.company_name = this.fullCustomer.company_name;
        }
        // Auto-select payment term for existing customer. Only when the field
        // is still blank — a quote loaded for editing already carries its own
        // payment method (FT-3) and the customer default must not stomp it.
        const paymentTermsId =
          this.storeOrder.customer_netsuite_payment_terms_id ??
          this.fullCustomer.payment_terms_id;
        if (paymentTermsId && !this.order.payment_details?.method) {
          this.order.payment_details!.method = paymentTermsId;
        }
      } catch (err) {
        console.error(
          "Error pre-populating checkout from NetSuite customer:",
          err
        );
      }
    } else {
      if (!this.paymentTerms.length) {
        try {
          this.paymentTerms = await loadPaymentTerms();
        } catch (err) {
          console.error("Error fetching payment terms:", err);
        }
      }
    }
    if (this.loader) {
      // Drop the reference as it's hidden — a later initialize that skips the
      // overlay (details already prefetched) must not re-hide a dead loader.
      const loader = this.loader;
      this.loader = null;
      setTimeout(() => loader.hide(), 300);
    }
  }

  get hasCustomer(): boolean {
    return !!this.storeOrder.customer_id;
  }

  get isNewCustomer(): boolean {
    return !this.storeOrder.customer_netsuite_id;
  }

  protected openCustomerModal(): void {
    EventBus.$emit("openCustomerModal");
  }

  protected async setNewCustomer(): Promise<void> {
    await this.assignCustomer({
      customer_id: "0",
      customer_name: "New Customer",
      customer_netsuite_id: undefined,
      customer_netsuite_contact_id: undefined,
      customer_netsuite_payment_terms_id: undefined,
      customer_netsuite_price_level_id: undefined,
      customer_netsuite_price_level_discount: undefined,
      order_id: null
    });
    const basePrice = this.storePriceLevels.find(l => l.name === "Base Price") ?? null;
    await this.addMultiplier(basePrice);
  }

  get availablePaymentTerms(): PaymentTerm[] {
    if (this.isNewCustomer) {
      return this.paymentTerms;
    }
    // Store value may be undefined for customers selected before this field was added (stale localStorage).
    // Fall back to the freshly-fetched fullCustomer data.
    const customerTermsId =
      this.storeOrder.customer_netsuite_payment_terms_id ??
      this.fullCustomer?.payment_terms_id;
    if (customerTermsId) {
      return this.paymentTerms.filter(t => t.id === customerTermsId);
    }
    return this.paymentTerms;
  }

  protected activePanel = 0;

  protected switchPanel(index: number): void {
    this.activePanel = index;
  }

  protected togglePanel(index: number, val: boolean): void {
    this.activePanel = val ? index : -1;
  }

  protected setBillingInfo(same: boolean): void {
    if (same) {
      const shipping = this.order.shipping_details;
      if (!shipping) return;
      this.order.billing_details = Object.assign(
        {},
        { ...this.order.billing_details },
        {
          addressee: shipping.addressee,
          attention: shipping.attention,
          address: Object.assign({}, shipping.address),
          phone: shipping.phone,
          email: shipping.email
        }
      );
    } else {
      this.order.billing_details = blankBillingDetails();
    }
  }

  /**
   * The validated sections in the order they appear, since the accordion puts
   * Billing and Shipping in a different order for a new customer.
   */
  private get sections(): Array<{
    key: CheckoutSection;
    panel: number;
    title: string;
  }> {
    const contact = {
      key: "contact" as const,
      panel: 0,
      title: "Quote Contact"
    };
    const billing = { key: "billing" as const, title: "Billing Information" };
    const shipping = {
      key: "shipping" as const,
      title: "Shipping Information"
    };
    const payment = {
      key: "payment" as const,
      panel: 4,
      title: "Payment Terms"
    };
    return this.isNewCustomer
      ? [contact, { ...shipping, panel: 1 }, { ...billing, panel: 2 }, payment]
      : [contact, { ...billing, panel: 1 }, { ...shipping, panel: 2 }, payment];
  }

  protected async goToReview(): Promise<void> {
    EventBus.$emit("submitCheckoutForm");
    // A section can be invalid on a field the user can't see — a collapsed
    // panel only gets a red outline, which reads as the button being dead
    // (FUPOF-191). Open the first offending section and say what's wrong.
    const invalidSections = this.sections.filter(s => this.error[s.key]);
    if (invalidSections.length) {
      this.activePanel = invalidSections[0].panel;
      NotificationService.show(
        `Please complete the required fields in ${invalidSections
          .map(s => s.title)
          .join(", ")} before reviewing this quote.`,
        "warning"
      );
      this.$nextTick(() => {
        document
          .querySelector(".accordion-error")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    await this.saveDataToStore();
    // Cache API data so it can be restored without re-fetching when navigating
    // back — keyed to this customer so a later checkout for a different
    // customer doesn't reuse it (FUPOF-191).
    cachedFullCustomer = this.fullCustomer;
    cachedPaymentTerms = this.paymentTerms;
    cachedForCustomerId = this.storeOrder.customer_id ?? "";
    this.$router.push({
      name: "OrderReview",
      params: {
        order: this.order as any,
        paymentTerms: this.paymentTerms as any
      }
    });
  }

  protected async saveDataToStore(): Promise<void> {
    const shipping = this.order.shipping_details;
    if (shipping && !shipping.label) {
      shipping.label = shipping.address?.address_1 ?? "";
    }

    await this.addShipping(this.order.shipping_details);
    await this.addBilling(this.order.billing_details);
    await this.addPayment(this.order.payment_details);
    await this.addContact(this.order.contact_details);
    await this.addNotes(this.order.notes);
  }

  protected async prepQuoteData(): Promise<void> {
    try {
      await this.saveDataToStore();
      const order = await this.parseItems();
      // FUPOF-191: create vs update is decided by the draft's
      // netsuite_quote_id inside the submit flow — one path for both.
      await this.createQuote(order);
    } catch (err) {
      GenericError.popup("Something went wrong.");
    }
  }

  /**
   * The API expects only mfr_codes for fabric swatches. We save the entire fabric swatch object on the frontend because the fabric selector needs the extra data to do filtering
   */
  protected async parseItems(): Promise<Order> {
    const items = JSON.parse(JSON.stringify(this.storeOrder.items!));
    for (const item of items) {
      // Lines added directly in NetSuite carry no configuration, or a
      // best-effort SKU-parsed one whose fabrics are already bare mfr codes.
      // There is nothing to parse, and dereferencing it would throw and block
      // the submit — skip them so the quote can still go through.
      if (item.ns_locked || !item.item_details) {
        continue;
      }
      if (item.product_type === ProductType.Umbrella) {
        const parsedData = this.parseFabricFields(
          (item.item_details as CustomUmbrellaOrder)
            .canopy as CustomUmbrellaOrder["canopy"]
        );
        (item.item_details as CustomUmbrellaOrder).canopy = Object.assign(
          {},
          { ...(item.item_details as CustomUmbrellaOrder).canopy },
          { ...parsedData }
        );
      } else if (item.product_type === ProductType.Chair) {
        const parsedData = this.parseFabricFields(
          item.item_details as CustomChairOrder
        );
        (item.item_details as any) = Object.assign(
          {},
          { ...item.item_details },
          { ...parsedData } as ChairOrderRecord
        );
      }
    }
    const order = { ...this.storeOrder };
    order.items = [...items];
    return order;
  }

  /**
   * API is expecting strings for fabrics. parseFabricFields() convert swatch objects to strings if value is not already a string
   */
  protected parseFabricFields(
    fabric: CustomUmbrellaOrder["canopy"] | CustomChairOrder
  ): UmbrellaOrderRecord | ChairOrderRecord {
    const fabrics = [
      "mainFabric",
      "mainTrimFabric",
      "mainTrimFabricInner",
      "ventTopFabric",
      "ventMiddleFabric",
      "ventTrimFabric",
      "valanceFabric",
      "fringeFabric",
      "bindingFabric",
      "ribFabric",
      "undersideFabric"
    ];
    const parsed = {} as UmbrellaOrderRecord;
    for (const fabricKey of fabrics) {
      if (fabric[fabricKey] && fabric[fabricKey].mfr_code) {
        parsed[fabricKey] = fabric[fabricKey].mfr_code;
      } else if (
        fabric[fabricKey] &&
        Array.isArray(fabric[fabricKey]) &&
        fabric[fabricKey].length > 0
      ) {
        parsed[fabricKey] = fabric[fabricKey].map(swatch => {
          if (swatch.mfr_code) {
            return swatch.mfr_code;
          } else {
            return swatch;
          }
        });
      } else {
        parsed[fabricKey] = fabric[fabricKey];
      }
    }
    return parsed;
  }

  protected async createQuote(order: Order): Promise<void> {
    try {
      // ToDo: FNSI-101 - create NetSuite quote and then create unofficial quote.
      // commenting out the unofficial quote creation for now until we optimize the auth process and can associate quotes with users properly
      // DETERMINING IF WE CAN ELIMINATE THE "UNOFFICIAL" QUOTE CONCEPT ALTOGETHER AND JUST HAVE NETSUITE QUOTES
      const res = await this.createNetSuiteQuote(order);
      // const quote = await this.orderService.createQuote(order);
      // await this.addOrderId({ id: quote._id!, creator: quote.creator_id });
      const query: Record<string, string> = { type: "quote" };
      if (order.contact_details?.send_email) {
        query.emailSent = "1";
      }
      if (res?.estimate?.estimateId) {
        query.quoteId = String(res.estimate.estimateId);
      }
      if (res?.estimate?.tranId) {
        query.tranId = res.estimate.tranId;
      }
      await this.$router.push({ path: "/checkout/submitted", query });
    } catch (err) {
      GenericError.popup(err.message);
    }
  }

  protected async createOrder(order: Order): Promise<void> {
    try {
      // ToDo: FNSI-101 - create NetSuite quote, request for that quote to be submitted, and then create the unofficial order through the Node API
      // DETERMINING IF WE CAN ELIMINATE THE "UNOFFICIAL" ORDER CONCEPT ALTOGETHER AND JUST HAVE NETSUITE ORDERS
      const res = await this.orderService.createOrder(order);
      this.$router.push("/checkout/submitted?type=order");
    } catch (err) {
      GenericError.popup(err.message);
    }
  }

  protected async convertToOrder(order: Order): Promise<void> {
    try {
      const res = await this.orderService.convertOrderToQuote(
        order._id!,
        order
      );
      this.$router.push("/checkout/submitted?type=order");
    } catch (err) {
      GenericError.popup(err.message);
    }
  }

  protected receiveError(
    invalid: boolean,
    component: "billing" | "shipping" | "payment" | "contact"
  ) {
    if (
      this.isNewCustomer &&
      (component === "billing" || component === "payment")
    ) {
      this.error[component] = false;
      return;
    }
    this.error[component] = invalid;
  }
}
